import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  OnInit,
  OnDestroy,
  DestroyRef,
  signal,
  resource,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  AfterViewInit,
  HostListener,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { CeIconComponent } from '@enginuity/core/atoms/ce-icon/ce-icon.component';
import { AvatarIconComponent } from '@enginuity/core/molecules/avatar-icon/avatar-icon.component';
import { GlobalSearchService } from '@services/core-services/global-search.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { GlobalLoaderWrapperComponent } from '@enginuity/analytics/organisms/global-loader-wrapper/global-loader-wrapper.component';
import { GlobalCommandsService } from '@services/core-services/global-command.service';
import { ApplicationMode, AppService, ThemeMode } from '@services/core-services/app.service';
import { AuthService } from '@services/auth-services/auth.service';
import { NotifyService } from '@services/core-services/notify.service';
import { CmdkModule } from '@ngxpert/cmdk';
import { Subscription, firstValueFrom } from 'rxjs';
import { debounceSignal } from '@services/core-services/utils/signal.debounce.utils';
import { BlocksComponent } from '../blocks/blocks.component';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GlobalLoaderService, LoaderState } from '@services/core-services/global-loader.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ViewStatus } from '@services/ui-services/models/view-status.enum';
import { OrderStatus } from '@services/order-services/models/order-status';
import { PlatformUtils } from '@services/core-services/platform.utils';
import { ShortcutsService } from '@services/core-services/shortcuts.service';
import { ShortcutsUtils } from '@services/core-services/shortcuts.constants';
import { ActionBtnComponent } from '@enginuity/core/molecules/action-btn/action-btn.component';

interface CommandItem {
  name: string;
  icon: string;
  index: string;
  key?: string;
  shortcut?: string[];
  isToggle?: boolean;
  platformKeys?: {
    mac: string[];
    windows: string[];
  };
}

interface SuggestionItem {
  name: string;
  icon: string;
  index: string;
  key?: string;
  shortcut?: string[];
}

interface RefreshIndexItem {
  name: string;
  icon: string;
  index: string;
}

@Component({
  selector: 'app-active-search',
  templateUrl: './active-search.component.html',
  styleUrls: ['./active-search.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CeIconComponent,
    AvatarIconComponent,
    CmdkModule,
    MatDialogModule,
    GlobalLoaderWrapperComponent,
    BlocksComponent,
    FormsModule,
    MatTooltipModule,
    ActionBtnComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ActiveSearchComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('searchInput') searchInput?: ElementRef;
  @Input() autoFocus: boolean = true;
  private destroyRef = inject(DestroyRef);
  private globalSubscription: Subscription | undefined;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly loaderService = inject(GlobalLoaderService);
  public applicationMode: ApplicationMode = this.appService.getApplicationMode();
  public navigations = ShortcutsUtils.getNavigationItems();
  public commands: CommandItem[] = ShortcutsUtils.getCommandItems();
  public suggestions: SuggestionItem[] = ShortcutsUtils.getSuggestionItems();
  public refreshIndexData: RefreshIndexItem[] = [
    { name: 'Refresh Customer Index', icon: 'person', index: 'Customers' },
    { name: 'Refresh Order Index', icon: 'cart', index: 'Orders' },
    { name: 'Refresh Product Index', icon: 'box', index: 'Products' },
  ];
  private readonly dialog = inject(MatDialog);
  @Output() onClose: EventEmitter<any> = new EventEmitter<any>();
  @Input() showTabs: any = 'all';
  value = 'Open Customer Search';
  pages: Array<string> = ['home'];
  searchQuery = signal('');
  activeIndex = signal<string | null>(null);
  debouncedSearchQuery = debounceSignal(this.searchQuery, 400);
  showApiResults = signal(false);
  hasPotentialResults = signal(false);
  hasResults = signal(false);

  // Computed signal to check if there are any local matches
  hasLocalMatches = computed(() => {
    const query = this.searchQuery();
    if (!query) return false;

    const filteredSuggestions = this.getFilteredItems(this.suggestions, query);
    const filteredCommands = this.getFilteredItems(this.commands, query);
    const filteredNavigations = this.getFilteredItems(this.navigations, query);

    return (
      filteredSuggestions.length > 0 ||
      filteredCommands.length > 0 ||
      filteredNavigations.length > 0
    );
  });

  // Modified computed signal to only trigger API when needed
  shouldTriggerApi = computed(() => {
    const query = this.debouncedSearchQuery();
    const hasLocal = this.hasLocalMatches();
    const showApi = this.showApiResults();

    // Trigger API if:
    // 1. There's a query AND no local matches, OR
    // 2. User explicitly requested API results (showApiResults is true)
    return query && (!hasLocal || showApi);
  });

  currentTheme = signal<'light' | 'dark'>('light');
  currentMode = signal<'live' | 'sandbox'>(
    this.appService.getApplicationMode() === ApplicationMode.Live ? 'live' : 'sandbox'
  );
  searchResource = resource({
    params: () => (this.shouldTriggerApi() ? this.debouncedSearchQuery() : null),
    loader: async ({ params }) => {
      const search = params;
      if (!search) return undefined;

      // Skip API calls for command states
      const currentIndex = this.activeIndex();
      if (currentIndex && ['Mode', 'Theme', 'RefreshIndex'].includes(currentIndex)) {
        return undefined;
      }

      const query: any = { search };
      if (this.activatedurl) {
        query['index'] = this.activeIndex();
        query['limit'] = 1000;
      } else if (this.activeIndex()) {
        query['index'] = this.activeIndex();
      }
      const results = await firstValueFrom(this.globalSearch.getSearchData(query));
      if (results.results && results.results.length > 0) {
        this.hasResults.set(true);
      }
      if (
        !results ||
        !results.results ||
        results.results.length === 0 ||
        !results.results[0].hits ||
        results.results[0].hits.length === 0
      ) {
        return undefined;
      }
      return results.results;
    },
  });
  // CONSTRUCTOR
  activatedurl: any;
  readonly ROOT_LEVEL_SECTIONS = ['Commands', 'Navigation', 'Suggestions'];
  readonly COMMAND_STATES = ['Mode', 'Theme'];
  readonly SEARCH_STATES = ['Customers', 'Orders', 'Products'];

  constructor(
    private readonly globalSearch: GlobalSearchService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly globalCommandsService: GlobalCommandsService,
    private readonly appService: AppService,
    private readonly authService: AuthService,
    private readonly notify: NotifyService,
    private readonly shortcutsService: ShortcutsService
  ) {
    const fullUrl = this.router.routerState.snapshot.url;
    const urlSegments = fullUrl.split('/');
    const targetSegment = urlSegments.find(segment =>
      ['customers', 'orders', 'products', 'category'].includes(segment)
    );
    this.activatedurl = targetSegment;
    if (targetSegment) {
      this.activeIndex.set(
        targetSegment.charAt(0).toUpperCase() + targetSegment.slice(1).toLowerCase()
      );
    }
    this.appService.theme$.pipe(takeUntilDestroyed()).subscribe(theme => {
      this.currentTheme.set(theme === ThemeMode.Light ? 'light' : 'dark');
    });
  }

  ngAfterViewInit() {
    if (this.autoFocus && this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.focus();
    }
    Promise.resolve().then(() => {
      this.cdr.detectChanges();
    });
  }

  // METHODS
  itemClick(event: any) {
    if (event.index === 'Shortcuts') {
      this.shortcutsService.showShortcuts();
      this.dialog.closeAll();
      return;
    }

    if (event.index === 'Theme') {
      this.toggleTheme();
      return;
    }

    if (event.index === 'Mode') {
      this.toggleMode();
      return;
    }

    if (event.index === 'RefreshCustomerIndex') {
      this.refreshIndex({ index: 'Customer' });
      return;
    }

    if (event.index === 'RefreshOrderIndex') {
      this.refreshIndex({ index: 'Order' });
      return;
    }

    if (event.index === 'RefreshProductIndex') {
      this.refreshIndex({ index: 'Product' });
      return;
    }

    const isNestedState = [...this.COMMAND_STATES, ...this.SEARCH_STATES].includes(event.index);

    if (isNestedState) {
      this.searchQuery.set('');
      const inputElement = document.querySelector('input[cmdkInput]') as HTMLInputElement;
      if (inputElement) {
        inputElement.value = '';
      }
    }

    this.activeIndex.set(event.index);
    if (event.index === 'Logout') {
      this.authService.logout().then(() => {
        this.router.navigate(['auth', 'login']);
      });
      this.dialog.closeAll();
    }
    this.cdr.detectChanges();
  }

  menuClick(event: any) {
    this.router.navigate([`/${event.route}`]);
    this.dialog.closeAll();
    this.cdr.detectChanges();
  }

  ngOnInit() {
    this.globalSubscription = this.globalCommandsService.shortcutTriggered.subscribe(command => {
      switch (command) {
        case 'clearSearch':
          this.clearSearch();
          break;
        case 'RefreshCustomerIndex':
          this.globalSearch
            .refreshCustomerIndex()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => this.notify.success('Refreshing customer index...'),
              error: err => this.notify.error('Failed to refresh customer index'),
            });
          break;
        case 'RefreshOrderIndex':
          this.globalSearch
            .refreshOrderIndex()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => this.notify.success('Refreshing order index...'),
              error: err => this.notify.error('Failed to refresh order index'),
            });
          break;
        case 'RefreshProductIndex':
          this.globalSearch
            .refreshProductIndex()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => this.notify.success('Refreshing product index...'),
              error: err => this.notify.error('Failed to refresh product index'),
            });
          break;
        case 'openSearch-Customers':
          this.setSearchContext('Customers');
          break;
        case 'openSearch-Orders':
          this.setSearchContext('Orders');
          break;
        case 'openSearch-Products':
          this.setSearchContext('Products');
          break;
      }
    });
  }

  ngOnDestroy() {
    if (this.globalSubscription) {
      this.globalSubscription.unsubscribe();
    }
  }

  clearSearch() {
    this.searchQuery.set('');
    this.activeIndex.set(null);
  }

  setSearchContext(context: string) {
    this.activeIndex.set(context);
    this.searchQuery.set('');
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.focus();
    }
    this.cdr.detectChanges();
  }

  setSearch(event: any) {
    const value = event.target.value;
    this.searchQuery.set(value);

    // Reset API results flag when user types new query
    this.showApiResults.set(false);

    if (this.COMMAND_STATES.includes(this.activeIndex() || '') && value) {
      this.activeIndex.set(null);
    }

    if (!value) {
      this.activeIndex.set(null);
    }

    Promise.resolve().then(() => {
      this.cdr.detectChanges();
    });
  }

  // New method to trigger API search explicitly
  triggerApiSearch() {
    this.showApiResults.set(true);
    // If there are no results, we'll hide the API results section
    if (!this.hasResults()) {
      this.showApiResults.set(false);
    }
    this.cdr.detectChanges();
  }

  refreshIndex(event: { index: string }) {
    // Clear any active search context
    this.activeIndex.set(null);
    this.searchQuery.set('');

    switch (event.index.toLowerCase()) {
      case 'customer':
        this.globalSearch.refreshCustomerIndex().subscribe();
        break;
      case 'order':
        this.globalSearch.refreshOrderIndex().subscribe();
        break;
      case 'product':
        this.globalSearch.refreshProductIndex().subscribe();
        break;
    }

    // Close the dialog after triggering the refresh
    this.dialog.closeAll();
  }

  handleThemeChange(isLight: boolean) {
    const newTheme = isLight ? 'light' : 'dark';
    if (newTheme !== this.currentTheme()) {
      this.currentTheme.set(newTheme);
      document.documentElement.classList.toggle('dark-theme');
      this.notify.success(`Switched to ${newTheme} theme`);
      this.dialog.closeAll();
    }
  }

  handleModeChange(isLive: boolean) {
    const newMode = isLive ? 'live' : 'sandbox';
    if (newMode !== this.currentMode()) {
      this.currentMode.set(newMode);
      this.appService.setApplicationMode(
        newMode === 'live' ? ApplicationMode.Live : ApplicationMode.Sandbox
      );
      this.notify.success(`Switched to ${newMode} mode`);
      this.dialog.closeAll();
    }
  }

  onOrder(event: any) {
    // Ensure change detection runs before navigation
    Promise.resolve().then(() => {
      this.router.navigate([`orders/${event}/summary`]);
      this.dialog.closeAll();
    });
  }

  onClick(event: any) {
    this.onClose.emit(event);
  }

  onCustomer(event: any) {
    this.router.navigate([`customers/${event}/overview`]);
    this.dialog.closeAll();
  }
  onProduct(event: any, variant: any = null) {
    this.router.navigate([`products/${event}/${variant ? 'variants' : 'overview'}`]);
    this.dialog.closeAll();
  }
  filterIndex(value: any, search: any): boolean {
    if (!search || !value) return true;
    if (typeof value === 'string') {
      return value.toLowerCase().includes(search.toLowerCase());
    }
    if (!value.name) return false;

    search = search.toLowerCase();
    return (
      value.name.toLowerCase().includes(search) ||
      (value.description?.toLowerCase() || '').includes(search) ||
      (value.key?.toLowerCase() || '').includes(search)
    );
  }

  isRootLevel(): boolean {
    return !this.activeIndex();
  }

  isCommandState(): boolean {
    const currentIndex = this.activeIndex();
    return currentIndex ? this.COMMAND_STATES.includes(currentIndex) : false;
  }

  isSearchState(): boolean {
    const currentIndex = this.activeIndex();
    return currentIndex ? this.SEARCH_STATES.includes(currentIndex) : false;
  }

  getFilteredRefreshIndex(): RefreshIndexItem[] {
    return this.refreshIndexData;
  }

  getFilteredItems(items: any[], search: string): any[] {
    if (!items || !Array.isArray(items)) return [];
    if (!search) return items;
    return items.filter(item => this.filterIndex(item, search));
  }

  getOrderStatus(orderStatus: OrderStatus) {
    switch (orderStatus) {
      case OrderStatus.Pending:
        return ViewStatus.Paused;
      case OrderStatus.Cancelled:
        return ViewStatus.Cancelled;
      case OrderStatus.PaymentFailed:
        return ViewStatus.Cancelled;
      case OrderStatus.AwaitingApproval:
        return ViewStatus.Paused;
      case OrderStatus.PartiallyScheduled:
        return ViewStatus.Paused;
      case OrderStatus.PartiallyShipped:
        return ViewStatus.Paused;
      case OrderStatus.ScheduledRequested:
        return ViewStatus.Draft;
      case OrderStatus.PartiallyDelivered:
        return ViewStatus.Paused;
      case OrderStatus.PartiallyUndelivered:
        return ViewStatus.Paused;
      case OrderStatus.PartiallyReturnToOrigin:
        return ViewStatus.Draft;
      case OrderStatus.AwaitingShipment:
        return ViewStatus.Live;
      case OrderStatus.Completed:
        return ViewStatus.Completed;
      case OrderStatus.Confirmed:
        return ViewStatus.Completed;
      default:
        return ViewStatus.White;
    }
  }

  onSearchQueryChange(query: string) {
    this.searchQuery.set(query);
  }

  toggleTheme() {
    this.appService.toggleThemeMode();
  }

  toggleMode() {
    const currentMode = this.appService.getApplicationMode();
    const newMode =
      currentMode === ApplicationMode.Live ? ApplicationMode.Sandbox : ApplicationMode.Live;
    this.updateModeLoaderMessages(currentMode);
    this.appService.setApplicationMode(newMode);
    this.currentMode.set(newMode === ApplicationMode.Live ? 'live' : 'sandbox');
    this.dialog.closeAll();
  }

  private updateModeLoaderMessages(currentMode: ApplicationMode) {
    if (currentMode === ApplicationMode.Live) {
      this.loaderService.setContent({
        title: '🔧 Switching to Sandbox Mode...',
        message:
          "Hold tight! We're setting up your sandbox for safe testing. You'll be back in the testing environment in no time!",
      });
    } else if (currentMode === ApplicationMode.Sandbox) {
      this.loaderService.setContent({
        title: '🚀 Switching to Live Mode...',
        message:
          "Get ready! We're powering up for live operations. This will just take a moment as we prepare everything for real-time action!",
      });
    }

    this.loaderService.setStatus(LoaderState.InProgress);
    setTimeout(() => {
      this.loaderService.setStatus(LoaderState.None);
    }, 1000);
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'Backspace' && !this.searchQuery()) {
      this.activeIndex.set(null);
      this.cdr.detectChanges();
      return;
    }

    if (event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
      const key = event.key.toLowerCase();

      if (key === 'x') {
        event.preventDefault();
        event.stopPropagation();
        this.shortcutsService.showShortcuts();
        this.dialog.closeAll();
        return;
      }

      // Command shortcuts that still work locally within search
      const commandMatch = this.commands.find(c => {
        if (!c.shortcut) return false;
        const shortcutKey =
          c.platformKeys?.[PlatformUtils.isMac() ? 'mac' : 'windows']?.[1] || c.shortcut[1];
        return shortcutKey.toLowerCase() === key;
      });

      if (commandMatch) {
        event.preventDefault();
        if (commandMatch.index === 'RefreshIndex') {
          this.activeIndex.set(commandMatch.index);
          this.searchQuery.set('');
          if (this.searchInput?.nativeElement) {
            this.searchInput.nativeElement.focus();
          }
        } else if (commandMatch.index === 'Theme') {
          this.toggleTheme();
        } else if (commandMatch.index === 'Mode') {
          this.toggleMode();
        } else if (commandMatch.index === 'Logout') {
          this.authService.logout().then(() => {
            this.router.navigate(['auth', 'login']);
          });
          this.dialog.closeAll();
        }
        this.cdr.detectChanges();
        return;
      }
    }
  }

  clearActiveIndex() {
    this.activeIndex.set(null);
    this.searchQuery.set('');
    if (this.searchInput) {
      this.searchInput.nativeElement.focus();
    }
  }

  onInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.searchQuery.set(value);
  }
}
